/**
 * PlanRendererInsolationMode — renders the floor plan SVG in Insolation mode.
 *
 * Responsibilities:
 *   - Mount the cached SVG clone with the insolation colour scheme applied.
 *   - Build a sun-light patches `<g>` layer inside `.apartment_tier`
 *     (Y-UP cartesian space) with a directional gradient.
 *   - Re-render patches in place when `insolationAngleDeg` changes (no remount).
 *   - Click a compartment → set focused compartment id → SunPathOverlay shows
 *     the sum of that compartment's windows' `sun_light_ranges`.
 *   - First-run onboarding overlay.
 *
 * 3D variant in V1: the patch overlay is skipped (the render-top texture is
 * the focus; insolation in 3D is a future iteration).
 */

import { useEffect, useRef, useMemo, useCallback, useState } from 'react'

import { useProduct } from '../../index.js'
import { VIEWER_VARIANT } from '../../constants/viewerVariant.js'
import { INSOLATION_DEFAULTS } from '../../constants/overlayDefaults.js'

import { applyRenderTopMutations }       from '../../utils/svgRenderTop.js'
import { applyInsolationColorMutations, applyInsolationHoverHighlight, getBucket } from '../../utils/svgInsolationColor.js'
import { attachStrokeGhost, removeStrokeGhost } from '../../utils/svgGhostStroke.js'
import {
  getDirectionalSunAngle,
  buildSunPatchPolygon,
  polygonToPath,
  isWindowIlluminatedAtAngle,
  pickFacadeEdge,
} from '../../utils/svgInsolation.js'

import { useInsolationWindows } from '../../hooks/useInsolationWindows.js'
import { useInsolationModePreview } from '../../hooks/useInsolationModePreview.js'
import { usePlanNorthRotation } from '../../hooks/usePlanNorthRotation.js'

import { SunPathOverlay }       from './SunPathOverlay.jsx'
import { CardinalsOverlay }     from './CardinalsOverlay.jsx'

import styles from './PlanRendererInsolationMode.module.scss'

const SVG_NS = 'http://www.w3.org/2000/svg'
const PATCHES_LAYER_ID = 'aplus-sun-patches'

// ── Patch layer mutation helpers (DOM-level, called from effects) ────────────

function _buildPatchGradient(id, segmentMid, farMid) {
  const gradient = document.createElementNS(SVG_NS, 'linearGradient')
  gradient.setAttribute('id', id)
  // userSpaceOnUse keeps the gradient axis aligned with the parallelogram
  // regardless of the patch's bounding-box aspect ratio. The axis runs from
  // the window-edge midpoint (dark) to the far-edge midpoint (transparent),
  // so the gradient "rotates" with each window.
  gradient.setAttribute('gradientUnits', 'userSpaceOnUse')
  gradient.setAttribute('x1', String(segmentMid[0]))
  gradient.setAttribute('y1', String(segmentMid[1]))
  gradient.setAttribute('x2', String(farMid[0]))
  gradient.setAttribute('y2', String(farMid[1]))

  const stopNear = document.createElementNS(SVG_NS, 'stop')
  stopNear.setAttribute('offset', '0%')
  stopNear.setAttribute('stop-color', 'rgb(255, 200, 0)')
  stopNear.setAttribute('stop-opacity', '0.7') // dark / visible at window
  const stopFar = document.createElementNS(SVG_NS, 'stop')
  stopFar.setAttribute('offset', '100%')
  stopFar.setAttribute('stop-color', 'rgb(255, 200, 0)')
  stopFar.setAttribute('stop-opacity', '0')    // fades into the room

  gradient.appendChild(stopNear)
  gradient.appendChild(stopFar)
  return gradient
}

function _getCompartmentCenter(svgElement, compartmentNumber) {
  if (compartmentNumber == null) return null
  const el = svgElement.querySelector(`path.compartment[number="${compartmentNumber}"]`)
  if (!el || typeof el.getBBox !== 'function') return null
  try {
    const bbox = el.getBBox()
    return [bbox.x + bbox.width / 2, bbox.y + bbox.height / 2]
  } catch {
    return null
  }
}

function _renderPatches({ svgElement, insolationAngleDeg, azimuthDeg, windows, focusedCompartmentId }) {
  if (!svgElement) return

  const directionalAngle = getDirectionalSunAngle(insolationAngleDeg, azimuthDeg)

  // Remove any prior layer (idempotent)
  const prior = svgElement.querySelector(`#${PATCHES_LAYER_ID}`)
  if (prior) prior.remove()

  if (!windows.length) return

  const layer = document.createElementNS(SVG_NS, 'g')
  layer.setAttribute('id', PATCHES_LAYER_ID)
  layer.setAttribute('class', 'aplus-sun-patches')

  const defs = document.createElementNS(SVG_NS, 'defs')
  layer.appendChild(defs)

  // Cache compartment centroids — many windows share a compartment.
  const centroidCache = new Map()
  const getCentroid = (n) => {
    if (centroidCache.has(n)) return centroidCache.get(n)
    const c = _getCompartmentCenter(svgElement, n)
    centroidCache.set(n, c)
    return c
  }

  let patchIndex = 0
  for (const win of windows) {
    // Filter by focused compartment, if any.
    if (focusedCompartmentId != null && Number(win.compartmentNumber) !== Number(focusedCompartmentId)) continue
    if (!isWindowIlluminatedAtAngle(win, insolationAngleDeg)) continue

    const centroid = getCentroid(win.compartmentNumber)
    // Build the parallelogram from the FACADE edge so the patch naturally spans
    // sill thickness + room interior in one shape. Final visible shape will be
    // produced by clipping (task 2.2) against (compartment path ∪ all sills).
    const facadeEdge = pickFacadeEdge(win.candidateEdges, centroid)
    if (!facadeEdge) continue

    const polygon = buildSunPatchPolygon(facadeEdge, directionalAngle, INSOLATION_DEFAULTS.patchDistance)
    if (!polygon || polygon.length < 4) continue

    // Gradient axis: facade-edge midpoint → that midpoint translated by s·d.
    const sunRad = (directionalAngle * Math.PI) / 180
    const sx = Math.cos(sunRad)
    const sy = Math.sin(sunRad)
    const segMid = [
      (facadeEdge[0][0] + facadeEdge[1][0]) / 2,
      (facadeEdge[0][1] + facadeEdge[1][1]) / 2,
    ]
    const farMid = [
      segMid[0] + sx * INSOLATION_DEFAULTS.patchDistance,
      segMid[1] + sy * INSOLATION_DEFAULTS.patchDistance,
    ]

    const gradientId = `aplus-sun-grad-${patchIndex}`
    defs.appendChild(_buildPatchGradient(gradientId, segMid, farMid))

    const path = document.createElementNS(SVG_NS, 'path')
    path.setAttribute('d', polygonToPath(polygon))
    path.setAttribute('fill', `url(#${gradientId})`)
    path.setAttribute('pointer-events', 'none')
    layer.appendChild(path)

    patchIndex += 1
  }

  const parent = svgElement.querySelector('.apartment_tier') ?? svgElement
  parent.appendChild(layer)
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * @param {{ svgElement: SVGElement|null, svgDimensions: { width: number, height: number } }} props
 */
export function PlanRendererInsolationMode({ svgElement, svgDimensions }) {
  const containerRef   = useRef(null)
  const mountedSvgRef  = useRef(null)
  const wrapperRef     = useRef(null)
  const hoverGhostRef     = useRef(null)
  const selectedGhostRef  = useRef(null)

  const { selection, viewerState, viewerActions } = useProduct()
  const {
    viewerVariant,
    hoveredCompartmentId,
    selectedCompartmentId,
    insolationAngleDeg,
    insolationFocusedCompartmentId,
    rotatedToNorth,
    insolationHoveredHour,
    selectedLevelId,
  } = viewerState
  const { setInsolationFocusedCompartmentId, setInsolationAngleDeg, setHoveredCompartmentId, setSelectedCompartmentId, setInsolationHoveredPathBucket } = viewerActions

  // ── On-entry preview (sun sweep 06:00 → 18:00 → 12:00) ────────────────────

  const [previewInteracted, setPreviewInteracted] = useState(false)

  const { product } = selection
  const refTier = selectedLevelId ?? product?.tiers?.[0]?.ref_tier ?? null
  const azimuthDeg = Number(product?.azimuth)

  // ── Rotate-to-North ───────────────────────────────────────────────────────

  const { wrapperStyle, rendererStyle, activeRotationDeg } = usePlanNorthRotation({
    rotatedToNorth,
    azimuth: product?.azimuth ?? null,
    svgDimensions,
    wrapperRef,
  })

  useInsolationModePreview({
    enabled: !!svgElement,
    userInteracted: previewInteracted,
    setInsolationAngleDeg,
    minAngleDeg: INSOLATION_DEFAULTS.minAngleDeg,
    maxAngleDeg: INSOLATION_DEFAULTS.maxAngleDeg,
    noonAngleDeg: INSOLATION_DEFAULTS.initialAngleDeg,
  })

  const productCompartments = useMemo(
    () => (Array.isArray(product?.compartments) ? product.compartments : []),
    [product],
  )

  const windows = useInsolationWindows({ svgElement, product, refTier })

  // ── Mount / re-mount SVG on element / variant change ─────────────────────

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.innerHTML = ''
    if (!svgElement) return

    const clone = svgElement.cloneNode(true)

    if (viewerVariant === VIEWER_VARIANT._3D) {
      // Keep windows visible — InsolationMode anchors sun-patch overlays to them
      applyRenderTopMutations(clone, { keepWindows: true })
    } else {
      // Initial paint respects any focus that may already be active when the
      // SVG element changes (e.g. tier swap while a compartment was focused).
      const list = insolationFocusedCompartmentId == null
        ? productCompartments
        : productCompartments.filter((c) => Number(c?.number) === Number(insolationFocusedCompartmentId))
      applyInsolationColorMutations(clone, list)
    }

    container.appendChild(clone)
    mountedSvgRef.current = svgElement
  }, [svgElement, viewerVariant, productCompartments]) // eslint-disable-line react-hooks/exhaustive-deps -- focus changes handled by the focus-color effect below

  // ── Focused compartment → only that one keeps its insolation fill ────────
  // Re-applies in place when focus changes, without a full SVG remount.

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    if (viewerVariant === VIEWER_VARIANT._3D) return
    const mountedSvg = container.querySelector('svg')
    if (!mountedSvg) return
    if (mountedSvgRef.current !== svgElement) return // mount effect will paint

    const list = insolationFocusedCompartmentId == null
      ? productCompartments
      : productCompartments.filter((c) => Number(c?.number) === Number(insolationFocusedCompartmentId))
    applyInsolationColorMutations(mountedSvg, list)
  }, [insolationFocusedCompartmentId, productCompartments, viewerVariant, svgElement])

  // ── Hover highlight effect (legend → plan) ──────────────────────────────
  // Both variants react — legend has semantic meaning regardless of 2D/3D.
  // 2D: mutates inline style.fill — -muted for non-matching, base for matching.
  // 3D: inline filter brightness — matching bucket stands out (1.05), others dim (0.88).

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    if (viewerVariant === VIEWER_VARIANT._3D) {
      const byNumber = {}
      for (const c of productCompartments) { byNumber[Number(c?.number)] = c }
      container.querySelectorAll('path.compartment').forEach((path) => {
        if (insolationHoveredHour == null) {
          path.style.filter = ''
        } else {
          const comp = byNumber[Number(path.getAttribute('number'))]
          const bucket = getBucket(comp)
          path.style.filter = bucket === insolationHoveredHour ? 'brightness(1.05)' : 'brightness(0.88)'
        }
      })
      return
    }
    const mountedSvg = container.querySelector('svg')
    if (!mountedSvg) return
    applyInsolationHoverHighlight(mountedSvg, productCompartments, insolationHoveredHour)
  }, [insolationHoveredHour, productCompartments, viewerVariant])

  // ── Render / re-render the sun patch layer ───────────────────────────────
  // Renders in both 2D and 3D — in 3D the windows are kept visible by
  // applyRenderTopMutations({ keepWindows: true }) so the patches still anchor
  // to real geometry on top of the raster underlay.

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const mountedSvg = container.querySelector('svg')
    if (!mountedSvg) return

    _renderPatches({
      svgElement: mountedSvg,
      insolationAngleDeg,
      azimuthDeg,
      windows,
      focusedCompartmentId: insolationFocusedCompartmentId,
    })
  }, [insolationAngleDeg, azimuthDeg, windows, viewerVariant, insolationFocusedCompartmentId])

  // ── Hover (pointer → setHoveredCompartmentId) ─────────────────────────────

  const handlePointerMove = useCallback((event) => {
    if (event.pointerType === 'touch') return
    const target = /** @type {Element|null} */ (event.target)
    if (!target) return
    const compartmentEl = target.closest('path.compartment')
    const n = compartmentEl ? Number(compartmentEl.getAttribute('number')) : null
    const isValid = n != null && Number.isFinite(n)
    setHoveredCompartmentId(isValid ? n : null)
    const comp = isValid ? productCompartments.find((c) => Number(c?.number) === n) : null
    setInsolationHoveredPathBucket(isValid ? getBucket(comp) : null)
  }, [setHoveredCompartmentId, setInsolationHoveredPathBucket, productCompartments])

  const handlePointerLeave = useCallback(() => {
    setHoveredCompartmentId(null)
    setInsolationHoveredPathBucket(null)
  }, [setHoveredCompartmentId, setInsolationHoveredPathBucket])

  // ── Hover ghost (SVG path highlight) ──────────────────────────────────────
  // 2D: ghost stroke only — insolation fills carry semantic meaning, brightness
  //     would distort the visual information/legend mapping.
  // 3D: inline filter brightness — paths show render-top texture, not insolation
  //     colours, so brightness is safe and no ghost is needed.

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    removeStrokeGhost(hoverGhostRef)
    // Clear any 3D brightness left from a previous hover
    container.querySelectorAll('path.compartment').forEach((p) => { p.style.filter = '' })
    if (hoveredCompartmentId == null) return
    const path = container.querySelector(`path.compartment[number="${hoveredCompartmentId}"]`)
    if (!path) return
    if (viewerVariant === VIEWER_VARIANT._3D) {
      path.style.filter = 'brightness(0.88)'
    } else {
      attachStrokeGhost(path, 'var(--bim-color-product-outline, var(--bim-color-hero-outlines))', '0.75em', hoverGhostRef)
    }
  }, [hoveredCompartmentId, svgElement, viewerVariant])

  // ── Click a compartment → focus it for SunPathOverlay ────────────────────

  const handleClick = useCallback((event) => {
    const target = /** @type {Element|null} */ (event.target)
    if (!target) return
    const compartmentEl = target.closest('path.compartment')
    if (!compartmentEl) {
      setInsolationFocusedCompartmentId(null)
      setSelectedCompartmentId(null)
      return
    }
    const number = Number(compartmentEl.getAttribute('number'))
    if (!Number.isFinite(number)) return
    const newFocused = number === insolationFocusedCompartmentId ? null : number
    setInsolationFocusedCompartmentId(newFocused)
    setSelectedCompartmentId(newFocused)
  }, [setInsolationFocusedCompartmentId, setSelectedCompartmentId, insolationFocusedCompartmentId])

  // ── Sync: external selectedCompartmentId → insolationFocusedCompartmentId ────────
  // When CompartmentsPane (or any other source) drives selectedCompartmentId,
  // mirror it into focus so SunPathOverlay follows.

  useEffect(() => {
    setInsolationFocusedCompartmentId(selectedCompartmentId)
  }, [selectedCompartmentId, setInsolationFocusedCompartmentId])

  // ── Selected ghost (both 2D and 3D) ───────────────────────────────────────
  // Ghost stroke is used in BOTH variants — in 3D, paths show render-top
  // texture so CSS `compartmentSelected` fill/stroke class has no visual effect.
  // A ghost stroke path renders on top of the texture as a visible outline.

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    removeStrokeGhost(selectedGhostRef)
    if (selectedCompartmentId == null) return
    const path = container.querySelector(`path.compartment[number="${selectedCompartmentId}"]`)
    if (!path) return
    attachStrokeGhost(path, 'var(--bim-color-product-active, var(--bim-color-hero-active))', '1em', selectedGhostRef)
  }, [selectedCompartmentId, svgElement, viewerVariant])

  // ── SunPathOverlay data ──────────────────────────────────────────────────
  // Focused compartment → use its `sun_light_ranges` directly.
  // No focus → use product-level sun_light_ranges (overall envelope).

  const overlaySunLightRanges = useMemo(() => {
    if (insolationFocusedCompartmentId != null) {
      const comp = productCompartments.find(
        (c) => Number(c?.number) === Number(insolationFocusedCompartmentId),
      )
      const ranges = Array.isArray(comp?.sun_light_ranges) ? comp.sun_light_ranges : []
      return ranges.filter((r) => Array.isArray(r) && r.length === 2)
    }
    const productRanges = Array.isArray(product?.sun_light_ranges) ? product.sun_light_ranges : []
    return productRanges.filter((r) => Array.isArray(r) && r.length === 2)
  }, [insolationFocusedCompartmentId, productCompartments, product])

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div ref={wrapperRef} className={styles.wrapper} style={wrapperStyle}>
      <div
        ref={containerRef}
        className={`${styles.renderer} bim-plan`}
        data-variant={viewerVariant}
        data-focused-compartment={insolationFocusedCompartmentId ?? ''}
        style={rendererStyle}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
        onClick={(e) => { if (!previewInteracted) setPreviewInteracted(true); handleClick(e) }}
        aria-label="Symulacja nasłonecznienia"
      />
      <SunPathOverlay
        insolationAngleDeg={insolationAngleDeg}
        azimuthDeg={azimuthDeg}
        sunLightRanges={overlaySunLightRanges}
        onAngleChange={(angle) => {
          setInsolationAngleDeg(angle)
          if (!previewInteracted) setPreviewInteracted(true)
        }}
      />
      <CardinalsOverlay azimuthDeg={azimuthDeg} northRotationDeg={activeRotationDeg} />
    </div>
  )
}
